import React, { useState, useEffect } from 'react';
import {
  ThemeProvider, CssBaseline, AppBar, Toolbar, Typography, Button,
  Tabs, Tab, Box, Container, Paper, Card, CardActionArea, Stack,
  BottomNavigation, BottomNavigationAction, useMediaQuery,
} from '@mui/material';
import CloudUploadIcon   from '@mui/icons-material/CloudUpload';
import ReceiptLongIcon   from '@mui/icons-material/ReceiptLong';
import BarChartIcon      from '@mui/icons-material/BarChart';
import PeopleIcon        from '@mui/icons-material/People';
import EventIcon         from '@mui/icons-material/Event';
import ChevronRightIcon  from '@mui/icons-material/ChevronRight';

import UploadReceipt  from './components/UploadReceipt';
import ReceiptList    from './components/ReceiptList';
import MemberManager  from './components/MemberManager';
import Dashboard      from './components/Dashboard';
import EventManager   from './components/EventManager';
import { getMembers } from './api';
import { GROUP_THEMES, DEFAULT_THEME } from './theme';

const VALID_GROUPS = ['Dolfijnen', 'Zeeverkenners', 'Wildevaart'];

const GROUP_ICONS = {
  Dolfijnen:     '🐬',
  Zeeverkenners: '⛵',
  Wildevaart:    '🌊',
};

const GROUP_PASSWORDS = {
  Dolfijnen:     'dolfijnen2026',
  Zeeverkenners: 'zeeverkenners2026',
  Wildevaart:    'wildevaart2026',
};

const GROUP_COLORS = {
  Dolfijnen:     '#667eea',
  Zeeverkenners: '#1a6baa',
  Wildevaart:    '#0f9b82',
};

const NAV_TABS = [
  { value: 'upload',    label: 'Bonnetje Uploaden',   shortLabel: 'Upload',    icon: <CloudUploadIcon /> },
  { value: 'list',      label: 'Bonnetjes Overzicht', shortLabel: 'Overzicht', icon: <ReceiptLongIcon /> },
  { value: 'dashboard', label: 'Dashboard & Export',  shortLabel: 'Export',    icon: <BarChartIcon /> },
  { value: 'members',   label: 'Stafleden Beheren',   shortLabel: 'Stafleden', icon: <PeopleIcon /> },
  { value: 'events',    label: 'Evenementen',          shortLabel: 'Events',    icon: <EventIcon /> },
];

function App() {
  const [activeGroup, setActiveGroup] = useState(() => {
    const saved = localStorage.getItem('selectedGroup');
    return VALID_GROUPS.includes(saved) ? saved : null;
  });
  const [members, setMembers]               = useState([]);
  const [activeTab, setActiveTab]           = useState('upload');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const isMobile    = useMediaQuery('(max-width:768px)');
  const activeTheme = GROUP_THEMES[activeGroup] || DEFAULT_THEME;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (activeGroup) loadMembers(); }, [activeGroup]);

  const loadMembers = async () => {
    try {
      const data = await getMembers(activeGroup);
      setMembers(data);
    } catch (err) {
      console.error('Fout bij laden van stafleden:', err);
    }
  };

  const handleSelectGroup = (group) => {
    localStorage.setItem('selectedGroup', group);
    setActiveGroup(group);
    setActiveTab('upload');
    setRefreshTrigger(0);
    setMembers([]);
  };

  const handleSwitchGroup = () => {
    localStorage.removeItem('selectedGroup');
    setActiveGroup(null);
    setMembers([]);
  };

  // ── Group selection screen ────────────────────────────────────────────────
  if (!activeGroup) {
    return (
      <ThemeProvider theme={DEFAULT_THEME}>
        <CssBaseline />
        <Box sx={{ minHeight: '100vh', bgcolor: 'grey.100' }}>
          <AppBar position="static" elevation={1}>
            <Toolbar>
              <Typography variant="h6" fontWeight={700}>
                📋 Declaraties - Waterscouting
              </Typography>
            </Toolbar>
          </AppBar>
          <Container maxWidth="xs" sx={{ py: 5 }}>
            <Typography variant="h5" align="center" fontWeight={700} gutterBottom>
              Kies jouw groep
            </Typography>
            <Typography variant="body2" align="center" color="text.secondary" mb={3}>
              Kies de groep om verder te gaan
            </Typography>
            <Stack spacing={2}>
              {VALID_GROUPS.map(group => (
                <Card
                  key={group}
                  elevation={1}
                  sx={{
                    border: '2px solid transparent',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      borderColor: GROUP_COLORS[group],
                      boxShadow: `0 4px 20px ${GROUP_COLORS[group]}40`,
                      transform: 'translateY(-2px)',
                    },
                    '&:active': { transform: 'scale(0.98)' },
                  }}
                >
                  <CardActionArea onClick={() => handleSelectGroup(group)} sx={{ p: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography fontSize="2.2rem" lineHeight={1}>{GROUP_ICONS[group]}</Typography>
                      <Typography variant="h6" fontWeight={600}>{group}</Typography>
                      <ChevronRightIcon sx={{ ml: 'auto', color: GROUP_COLORS[group] }} />
                    </Box>
                  </CardActionArea>
                </Card>
              ))}
            </Stack>
          </Container>
        </Box>
      </ThemeProvider>
    );
  }

  // ── Main app ──────────────────────────────────────────────────────────────
  return (
    <ThemeProvider theme={activeTheme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', bgcolor: 'grey.100' }}>

        {/* Header */}
        <AppBar position="sticky" elevation={2} sx={{ zIndex: 1200 }}>
          <Toolbar variant={isMobile ? 'dense' : 'regular'}>
            <Typography variant="h6" fontWeight={700} sx={{ flexGrow: 1 }}>
              {GROUP_ICONS[activeGroup]}{' '}
              {isMobile ? activeGroup : `Declaraties — ${activeGroup}`}
            </Typography>
            <Button
              color="inherit"
              size="small"
              variant="outlined"
              onClick={handleSwitchGroup}
              sx={{ borderColor: 'rgba(255,255,255,0.5)', fontSize: '0.75rem', minHeight: 34 }}
            >
              Wissel
            </Button>
          </Toolbar>

          {/* Desktop tabs (hidden on mobile) */}
          {!isMobile && (
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              textColor="inherit"
              indicatorColor="secondary"
              variant="scrollable"
              scrollButtons="auto"
              sx={{ bgcolor: 'primary.dark', minHeight: 48 }}
            >
              {NAV_TABS.map(tab => (
                <Tab
                  key={tab.value}
                  value={tab.value}
                  label={tab.label}
                  icon={tab.icon}
                  iconPosition="start"
                  sx={{ minHeight: 48 }}
                />
              ))}
            </Tabs>
          )}
        </AppBar>

        {/* Page content */}
        <Container maxWidth="lg" sx={{ py: 2, pb: isMobile ? '80px' : 3 }}>
          {activeTab === 'upload' && (
            <UploadReceipt
              members={members}
              activeGroup={activeGroup}
              onReceiptSaved={() => setRefreshTrigger(t => t + 1)}
            />
          )}
          {activeTab === 'list' && (
            <ReceiptList
              members={members}
              activeGroup={activeGroup}
              groupPassword={GROUP_PASSWORDS[activeGroup]}
              refreshTrigger={refreshTrigger}
            />
          )}
          {activeTab === 'dashboard' && (
            <Dashboard
              activeGroup={activeGroup}
              groupPassword={GROUP_PASSWORDS[activeGroup]}
              refreshTrigger={refreshTrigger}
            />
          )}
          {activeTab === 'members' && (
            <MemberManager
              members={members}
              activeGroup={activeGroup}
              groupPassword={GROUP_PASSWORDS[activeGroup]}
              onMembersUpdated={loadMembers}
            />
          )}
          {activeTab === 'events' && (
            <EventManager
              activeGroup={activeGroup}
              groupPassword={GROUP_PASSWORDS[activeGroup]}
            />
          )}
        </Container>

        {/* Mobile bottom navigation */}
        {isMobile && (
          <Paper
            elevation={8}
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 1300,
              pb: 'env(safe-area-inset-bottom)',
            }}
          >
            <BottomNavigation
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              showLabels
              sx={{ bgcolor: 'background.paper' }}
            >
              {NAV_TABS.map(tab => (
                <BottomNavigationAction
                  key={tab.value}
                  label={tab.shortLabel}
                  value={tab.value}
                  icon={tab.icon}
                />
              ))}
            </BottomNavigation>
          </Paper>
        )}

      </Box>
    </ThemeProvider>
  );
}

export default App;
